/**
 * Free Adoption API Client (free-to-play Phase 2).
 *
 * Mirrors campaignApi.ts conventions (withBase, parseJson, try/catch → {success:false,error}).
 */

import { API_BASE_URL } from '../config/apiConfig';
import { getSession } from '../auth/sessionStore';
import { getOrCreateSessionId } from './pvpApi';

const API_VERSION = 'v1';
const API_BASE_PATH = '/api';
const JSON_HEADERS = { 'Content-Type': 'application/json' } as const;

function withBase(path: string): string {
  return `${API_BASE_URL}${API_BASE_PATH}/${API_VERSION}${path}`;
}

async function parseJson<T>(response: Response): Promise<T> {
  return (await response.json()) as T;
}

export interface AdoptFreeResponse {
  success: boolean;
  petId?: string;
  existing?: boolean;
  error?: string;
}

export interface ClaimFreePetsResponse {
  success: boolean;
  /** Number of guest-session free pets migrated onto the caller's authenticated identity. */
  claimedPets?: number;
  /** True when off-chain progress (level/xp/stats) was migrated along with ownership. */
  progressMigrated?: boolean;
  /** True when the claim was refused because the account already owns a Suikamon. */
  ineligible?: boolean;
  /** Machine-readable refusal reason (e.g. 'account_has_pets') when `ineligible`. */
  reason?: string;
  error?: string;
}

/**
 * POST /adoption/adopt-free — grants (or returns the existing) free starter pet.
 * When logged in, the Bearer token + walletAddress link the pet to the caller's account
 * (connected wallets can start free, level-3 capped); logged out, it stays session-keyed.
 *
 * `walletAddress` is the connected acting wallet (any chain). Pass it even when there is NO
 * backend session/JWT: a wallet-connected-but-not-logged-in player must still key the pet
 * to their wallet, or /play — which lists by wallet address — never shows the new pet. Falls
 * back to the authenticated session address. Omitted only for a pure guest (no wallet).
 */
export async function adoptFree(
  speciesId: number,
  name: string,
  walletAddress?: string,
): Promise<AdoptFreeResponse> {
  try {
    const session = getSession();
    const effectiveWallet = walletAddress ?? session?.address;
    const response = await fetch(withBase('/adoption/adopt-free'), {
      method: 'POST',
      headers: {
        ...JSON_HEADERS,
        ...(session?.token ? { Authorization: `Bearer ${session.token}` } : {}),
      },
      body: JSON.stringify({
        speciesId,
        name,
        sessionId: getOrCreateSessionId(),
        ...(effectiveWallet ? { walletAddress: effectiveWallet } : {}),
      }),
    });
    return parseJson<AdoptFreeResponse>(response);
  } catch (error) {
    console.error('Failed to adopt free pet:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Network error' };
  }
}

/**
 * POST /adoption/link-free — re-key this browser's guest-session free pet(s) onto a
 * connected wallet WITHOUT a backend login. Call when a wallet is connected but the free
 * pet was adopted session-only; otherwise /play (which lists by wallet address) shows an
 * empty roster and the pet "appears then disappears". Best-effort — never throws.
 */
export async function linkFreePetToWallet(walletAddress: string): Promise<{ linked: number }> {
  try {
    const session = getSession();
    const response = await fetch(withBase('/adoption/link-free'), {
      method: 'POST',
      headers: {
        ...JSON_HEADERS,
        ...(session?.token ? { Authorization: `Bearer ${session.token}` } : {}),
      },
      body: JSON.stringify({ sessionId: getOrCreateSessionId(), walletAddress }),
    });
    const data = await parseJson<{ success: boolean; linked?: number }>(response);
    return { linked: data.success && typeof data.linked === 'number' ? data.linked : 0 };
  } catch (error) {
    console.error('Failed to link free pet to wallet:', error);
    return { linked: 0 };
  }
}

/**
 * In-flight claim promise, shared so concurrent callers collapse to a SINGLE request.
 * A guest→login transition can fire claimFreePets() from two places at once (the
 * AccountProvider background claim on `persist`, and the PlayPage "Sign up to save" CTA
 * effect). Both should observe the same result without hitting the endpoint twice.
 * Cleared when the request settles so a later, genuinely-new login can claim again.
 */
let inFlightClaim: Promise<ClaimFreePetsResponse> | null = null;

/**
 * POST /adoption/claim-free. Migrates this browser's guest-session free pets onto the
 * caller's authenticated identity/wallet. Requires Bearer auth. Concurrent calls are
 * deduped via `inFlightClaim` so the guest→login handoff never double-claims.
 */
export async function claimFreePets(): Promise<ClaimFreePetsResponse> {
  if (inFlightClaim) return inFlightClaim;

  inFlightClaim = (async () => {
    try {
      const token = getSession()?.token;
      const response = await fetch(withBase('/adoption/claim-free'), {
        method: 'POST',
        headers: {
          ...JSON_HEADERS,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          sessionId: getOrCreateSessionId(),
          walletAddress: getSession()?.address,
        }),
      });
      return await parseJson<ClaimFreePetsResponse>(response);
    } catch (error) {
      console.error('Failed to claim free pets:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Network error' };
    } finally {
      inFlightClaim = null;
    }
  })();

  return inFlightClaim;
}
